import { useDetailFunnelHealth } from "@/services/dashboard/detailFunnel";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { Funnel } from "@ant-design/plots";
import { BarChart3 } from "lucide-react";
import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

interface Props {
  startDate?: Date | null;
  endDate?: Date | null;
  userId?: string;
  userSelected?: string;
  projectSelected?: string;
  type?: string;
  region?: string;
  cordination?: string;
  principal?: string;
  master?: string;
}

function transformFunnelData(
  rawData: {
    statusName: string;
    campaign: string;
    region: string;
    reached: number;
    order: string;
  }[]
) {
  const result: {
    stage: string;
    type: string;
    value: number;
    color: string;
  }[] = [];

  rawData.forEach((item, index) => {
    result.push({
      stage: item.statusName,
      type: `${item.campaign} - ${item.region}`, // nombre visible en legend/labels
      value: item.reached,
      color: "", // se asignará después
    });
  });

  return result;
}

export default function FunnelMultipleCharts(props: Props) {
  const {
    startDate,
    endDate,
    userId,
    userSelected,
    projectSelected,
    type,
    region,
    cordination,
    principal,
  } = props;

  const filters = useMemo(
    () => ({
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
    }),
    [
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
    ]
  );

  const { data, isLoading, isError, error } = useDetailFunnelHealth(filters);

  const funnelData = useMemo(() => {
    if (!data) return [];
    return transformFunnelData(data);
  }, [data]);

  if (isLoading)
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );

  if (isError)
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {error instanceof Error
              ? error.message
              : "Error al cargar los datos"}
          </Alert>
        </CardContent>
      </Card>
    );

  // Obtener todas las series únicas
  const uniqueTypes = Array.from(new Set(funnelData.map((d) => d.type)));

  const COLORS = [
    "#1F3B4D",
    "#3A506B",
    "#264653",
    "#2A9D8F",
    "#8AB17D",
    "#4E9F3D",
    "#6C757D",
    "#7A3E48",
    "#5E548E",
    "#9D4EDD",
    "#E76F51",
    "#FFB703",
    "#FB8500",
    "#F4A261",
    "#E9C46A",
    "#D4A373",
    "#8D99AE",
    "#556B2F",
    "#3C6E71",
    "#6D597A",
  ];

  const typeColors: Record<string, string> = {};
  uniqueTypes.forEach((type, i) => {
    typeColors[type] = COLORS[i % COLORS.length];
  });

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <BarChart3 size={24} />
        <Typography variant="h6">Funnel por desarrollo</Typography>
      </Box>
      <Swiper
        spaceBetween={20}
        slidesPerView={1} // valor por defecto
        breakpoints={{
          0: {
            slidesPerView: 1, // móvil (ancho >= 0)
          },
          1024: {
            slidesPerView: 3, // laptop/desktop (ancho >= 1024px)
          },
        }}
        style={{ padding: "10px 0" }}
      >
        {uniqueTypes.map((type) => {
          const dataByType = funnelData.filter((d) => d.type === type);
          const config = {
            height: 300,
            data: dataByType,
            xField: "stage",
            yField: "value",
            color: typeColors[type],
            funnelShape: "pyramid",
            dynamicHeight: true,
            label: {
              style: { fill: "#fff", fontSize: 12 },
              text: (datum: any) => datum.value,
            },
            legend: false,
          };

          return (
            <SwiperSlide key={type}>
              <Box
                sx={{
                  width: "100%",
                  minWidth: 300,
                  height: 320,
                }}
              >
                <Typography variant="subtitle1" mb={1}>
                  {type}
                </Typography>
                <Funnel {...config} />
              </Box>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Paper>
  );
}
